import { computed, ref } from "vue";
import { api, getUser } from "@/util/api";

// Light/dark theme handling for the whole app.
//
// - naive-ui is flipped via its built-in dark theme (see App.vue).
// - Bootstrap 5.3 + our own dark overrides key off `data-bs-theme="dark"` on
//   the <html> element, which we toggle here.
//
// Source of truth is the user's saved account preference; we mirror it into
// localStorage so the choice applies instantly on the next load (and before we
// know who is logged in) without a flash of the wrong theme.

type Mode = "light" | "dark";

const STORAGE_KEY = "eas-theme";

const mode = ref<Mode>("light");

export const isDark = computed(() => mode.value === "dark");
export const themeMode = mode;

const systemPrefersDark = (): boolean =>
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;

const apply = (m: Mode): void => {
    mode.value = m;

    const el = document.documentElement;
    if (m === "dark") {
        el.setAttribute("data-bs-theme", "dark");
        el.classList.add("theme-dark");
    } else {
        el.removeAttribute("data-bs-theme");
        el.classList.remove("theme-dark");
    }
};

const readCached = (): Mode | null => {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "light" || v === "dark" ? v : null;
};

/**
 * Apply the best theme we can before authentication (cached choice, else the
 * device setting), then reconcile with the logged-in user's saved preference.
 */
export const initTheme = async (): Promise<void> => {
    apply(readCached() ?? (systemPrefersDark() ? "dark" : "light"));

    try {
        const user = await getUser();
        const pref = user?.theme_preference;
        if (pref === "light" || pref === "dark") {
            apply(pref);
            localStorage.setItem(STORAGE_KEY, pref);
        }
    } catch {
        // Not logged in / offline — keep the cached or device-based choice.
    }
};

export const setTheme = async (m: Mode): Promise<void> => {
    apply(m);
    localStorage.setItem(STORAGE_KEY, m);

    try {
        await api.post("user/theme", { theme: m });
        if (window.appConfig?.user) {
            window.appConfig.user.theme_preference = m;
        }
    } catch {
        // Saving to the account failed (e.g. offline) — the local choice still
        // applies; it will re-sync next time the theme is changed.
    }
};

export const toggleTheme = (): Promise<void> =>
    setTheme(isDark.value ? "light" : "dark");
